import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { LazyLoadEvent } from 'primeng/api';
import { BaseComponent } from 'src/app/shared/base.component';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { PagedResult, ResultOfT } from 'src/app/models/response/result';
import { UserInfoResponseModel } from 'src/app/models/response/userInfo-response';
import { BatchUsageTrackingResponseModel } from 'src/app/models/response/batch-usage-tracking-response';
import { GetBatchUsageTrackingRequestModel } from 'src/app/models/request/batch-usage-tracking-request';
import { CompanyResponseModel } from 'src/app/models/response/company-response';

@Component({
  selector: 'app-all-batches',
  templateUrl: './all-batches.component.html',
  styleUrls: ['./all-batches.component.sass']
})
export class AllBatchesComponent extends BaseComponent implements OnInit, OnDestroy {
  private unsubscriber = new Subject<boolean>();

  Menustatus: boolean = false;
  filterResposniveEvent: boolean = false;
  isCustomerAdmin: boolean = false;

  // Messages / loader
  displayMessage: boolean = false;
  messageClass: boolean = false;
  message: string = '';
  loading: boolean = false;

  // Filters
  customerOptions: { label: string; value: number; }[] = [];
  selectedCustomerId: number | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  showClearFilter: boolean = false;
  displayClearButton: boolean = false;

  // Grid + pagination
  batches: BatchUsageTrackingResponseModel[] = [];
  pageNumber: number = 1;
  pageSize: number = 50;
  totalRecords: number = 0;
  isLazyLoadingEnabled: boolean = false;
  sortField: string = 'BatchDate';
  searchText: string = '';
  private searchTimer: any;
  sortOrder: number = -1;

  constructor(private userService: UserService) {
    super();
  }

  ngOnInit(): void {
    this.getAllCustomerAdmins();
    setTimeout(() => this.triggerInitialLoad(), 100);
  }

  // ────────────────────────────────────────
  // Customers (filter dropdown)
  // ────────────────────────────────────────
  getAllCustomerAdmins(): void {
      this.loading = true;
      this.userService.GetAllCompanyAdmins()
        .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
        .subscribe((result: ResultOfT<CompanyResponseModel[]>) => {
          this.processResult<any>(result, () => {
            if (result.IsSuccess) {
              this.customerOptions = (result.ResponseModel || []).map((c: CompanyResponseModel) => ({
                label: c.CompanyName,
                value: c.CompanyId
              }));
            } else {
              this.timeoutFunct(result.Message, false);
            }
          });
        }, (httpResponseError) => {
          this.loading = false;
          this.timeoutFunct(httpResponseError.message, false);
        });
    }

  // ────────────────────────────────────────
  // Batches list with lazy pagination
  // ────────────────────────────────────────
  triggerInitialLoad(): void {
    this.onLazyLoad(this.buildFirstPageEvent());
  }

  private buildFirstPageEvent(): LazyLoadEvent {
    return { first: 0, rows: this.pageSize } as LazyLoadEvent;
  }

  onLazyLoad(event: LazyLoadEvent): void {
    if (!this.batches) {
      this.batches = [];
    }

    const incomingField = event.sortField || this.sortField;
    const incomingOrder = event.sortField ? (event.sortOrder || 1) : this.sortOrder;
    const sortChanged = incomingField !== this.sortField || incomingOrder !== this.sortOrder;
    this.sortField = incomingField;
    this.sortOrder = incomingOrder;

    if (sortChanged || !event.rows || event.rows == this.pageSize) {
      // First page / filter change → reset
      this.batches = [];
      this.pageNumber = 1;
      this.totalRecords = 0;
      this.isLazyLoadingEnabled = false;
    } else {
      // Scrolled → load next page
      this.isLazyLoadingEnabled = true;
      const totalLoadedRecords = this.batches.length || 0;
      if (totalLoadedRecords >= this.totalRecords && this.batches.length > 0) {
        return;
      }
      this.pageNumber = Math.floor(totalLoadedRecords / this.pageSize) + 1;
    }
    this.loading = true;

    const requestModel = new GetBatchUsageTrackingRequestModel();
    requestModel.PageNumber = this.pageNumber;
    requestModel.PageSize = this.pageSize;
    requestModel.SortColumn = this.sortField;
    requestModel.SearchText = this.getSearchTerm();
    requestModel.SortOrder = this.sortOrder === 1 ? 'asc' : 'desc';
    requestModel.CustomerId = this.selectedCustomerId || null;
    requestModel.FromDate = this.toApiDate(this.fromDate);
    requestModel.ToDate = this.toApiDate(this.toDate);

    this.userService.getAllBatchUsageTracking(requestModel)
      .pipe(takeUntil(this.unsubscriber), finalize(() => this.loading = false))
      .subscribe((result: ResultOfT<PagedResult<BatchUsageTrackingResponseModel>>) => {
        this.processResult<any>(result, () => {
          if (result.IsSuccess) {
            if (result.ResponseModel && result.ResponseModel.Data) {
              this.totalRecords = result.ResponseModel.TotalRecords || 0;
              const newRecords = result.ResponseModel.Data;
              this.batches = this.pageNumber === 1
                ? [...newRecords]
                : [...this.batches, ...newRecords];
              this.isLazyLoadingEnabled = true;
              if (this.batches.length >= this.totalRecords) {
                this.sortLocally();
              }
            }
          } else {
            this.timeoutFunct(result.Message, false);
          }
        });
      }, (httpResponseError) => {
        this.loading = false;
        this.timeoutFunct(httpResponseError.message, false);
      });
  }

  private sortLocally(): void {
    const field = this.sortField;
    const dir = this.sortOrder;
    const numeric = ['BatchId', 'TotalDocuments', 'TotalPages'];
    this.batches = [...this.batches].sort((a: any, b: any) => {
      let x = a[field];
      let y = b[field];
      if (x == null) { return 1; }
      if (y == null) { return -1; }
      if (field === 'BatchDate') {
        x = new Date(x).getTime();
        y = new Date(y).getTime();
      } else if (numeric.indexOf(field) >= 0) {
        x = Number(String(x).replace(/,/g, ''));
        y = Number(String(y).replace(/,/g, ''));
      } else {
        x = String(x).toLowerCase();
        y = String(y).toLowerCase();
      }
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.onLazyLoad(this.buildFirstPageEvent()), 400);
  }

  // Accepts "BATCH-0012", "0012", "12" or a customer name
  private getSearchTerm(): string | null {
    const text = (this.searchText || '').trim();
    if (!text) { return null; }
    const m = /^(?:batch-?\s*)?(\d+)$/i.exec(text);
    return m ? (m[1].replace(/^0+/, '') || '0') : text;
  }

  applyFilters(): void {
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
      this.timeoutFunct('Batch date to must be after batch date from', false);
      return;
    }
    this.showClearFilter = true;
    this.displayClearButton = true;
    this.filterResposniveEvent = false;
    this.onLazyLoad(this.buildFirstPageEvent());
  }

  resetFilter(): void {
    this.selectedCustomerId = null;
    this.fromDate = null;
    this.toDate = null;
    this.showClearFilter = false;
    this.displayClearButton = false;
    this.filterResposniveEvent = false;
    this.onLazyLoad(this.buildFirstPageEvent());
  }

  // Company name looked up from customerOptions by the batch's CompanyId
  getCompanyName(batch: BatchUsageTrackingResponseModel): string {
    const id = batch?.CompanyId ?? batch?.CustomerId;
    const match = this.customerOptions.find(c => String(c.value) === String(id));
    return match?.label || batch?.CustomerName || '';
  }

  formatBatchId(id: number | string): string {
    if (id === null || id === undefined || id === '') { return ''; }
    return `BATCH-${String(id).padStart(4, '0')}`;
  }

  private toApiDate(value: Date | null): string | null {
    return value ? formatDate(value, 'yyyy-MM-dd', 'en-US') : null;
  }

  // ────────────────────────────────────────
  // UI helpers
  // ────────────────────────────────────────
  showMenu(): void {
    this.Menustatus = !this.Menustatus;
  }

  filterClickEvent(): void {
    this.filterResposniveEvent = !this.filterResposniveEvent;
  }

  timeoutFunct(msg: string, isSuccess: boolean): void {
    this.displayMessage = true;
    this.messageClass = isSuccess;
    this.message = msg;
    setTimeout(() => {
      this.message = '';
      this.displayMessage = false;
    }, environment.messageLifeSpan);
  }

  closeMessagePopup(): void {
    this.displayMessage = false;
    this.message = '';
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
    if (this.unsubscriber) {
      this.unsubscriber.next(true);
      this.unsubscriber.complete();
    }
  }
}
